import React, { useState } from 'react';
import { getStatusBadgeClass, getStatusText } from './OrderStatus';
import './OrderCard.css';
import { normalizePartyNameForDisplay } from '../../utils/platformUtils';
import FileBackupStatus from '../FileBackupStatus';
import { useTranslation } from '../../hooks/useTranslation';

function OrderCard({
  order,
  userRole,
  currentTab,
  creatorNames,
  user,
  onEdit,
  onApprove,
  onCancel,
  onUploadBill,
  onReplaceBill,
  onDispatch,
  onReadyToDispatch,
  onUploadBilty,
  onReplaceBilty,
  onUploadChallan,
  onReplaceChallan,
  onUploadProduct,
  onReplaceProduct,
  setBillModalUrl,
  setBiltyModalUrl,
  setChallanModalUrl,
  setProductModalUrl,
  setOrderDetailsModalUrl,
  setCurrentOrderId,
  viewChallan,
  viewBill,
  viewBilty,
  organizationData,
  onOpenApprovalModal,
  highlightOrderId
}) {
  const { t } = useTranslation('orders');
  const [isExpanded, setIsExpanded] = useState(false);
  const isDispatchedOrder = order.status === 'DISPATCHED';
  const isHighlighted = highlightOrderId === order.id;

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    
    // If it's a Firestore timestamp with toDate method
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      return dateValue.toDate().toLocaleString('en-US', { 
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true 
      });
    }
    
    // If it's a regular Date object or timestamp in milliseconds
    if (dateValue instanceof Date || typeof dateValue === 'number') {
      const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
      return date.toLocaleString('en-US', { 
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true 
      });
    }
    
    // If it's a server timestamp that hasn't been resolved yet
    if (dateValue === null) {
      return t('justNow');
    }
    
    // For any other case
    return String(dateValue);
  };

  const renderOrderDetails = (order) => {
    if (order.orderDetailsImageURLs && Array.isArray(order.orderDetailsImageURLs) && order.orderDetailsImageURLs.length > 0) {
      return (
        <div className="order-details">
          <div className="info-value">
            <div className="image-container-multiple">
              {order.orderDetailsImageURLs.map((url, idx) => (
                <div key={idx} className="image-thumb-item">
                  <img
                    src={url}
                    alt={`Order details ${idx + 1}`}
                    style={{ maxWidth: '100px', maxHeight: '100px', margin: 4, borderRadius: 8, cursor: 'pointer' }}
                    onClick={() => {
                      setOrderDetailsModalUrl(order.orderDetailsImageURLs);
                      setCurrentOrderId(order.id);
                    }}
                  />
                </div>
              ))}
            </div>
            <div className="order-card-image-actions">
                  <button
                onClick={() => {
                  setOrderDetailsModalUrl(order.orderDetailsImageURLs);
                  setCurrentOrderId(order.id);
                }}
                className="view"
              >
                {t('actions.viewEditImages')}
              </button>
            </div>
          </div>
        </div>
      );
    }
    // fallback to old single image or text
    if (order.orderDetailsImageURL) {
      return (
        <div className="order-details">
          <div className="info-value">
            <div className="image-container">
              <img 
                src={order.orderDetailsImageURL} 
                alt="Order details" 
                style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', cursor: 'pointer' }}
                onClick={() => {
                  setOrderDetailsModalUrl(order.orderDetailsImageURL);
                  setCurrentOrderId(order.id);
                }}
              />
            </div>
            <div className="order-card-image-actions">
              <button 
                onClick={() => {
                  setOrderDetailsModalUrl(order.orderDetailsImageURL);
                  setCurrentOrderId(order.id);
                }} 
                className="view"
              >
                {t('actions.viewEditImage')}
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="order-details">
        <div className="info-value">{order.orderDetails}</div>
      </div>
    );
  };

  const canViewBill = () => {
    if (userRole === 'InventoryManager') {
      return organizationData?.allowInventoryManagerBillAccess ?? true;
    }
    return true;
  };

  const handleBillButtonClick = () => {
    const processedUrls = order.billURLs.map(url => viewBill(url));
    setBillModalUrl(processedUrls);
    setCurrentOrderId(order.id);
  };

  const handleOrderValueClick = () => {
    if (onOpenApprovalModal && order) {
      onOpenApprovalModal(order);
    }
  };

  const getOrderValueClassName = () => {
    const baseClass = 'order-value';
    if (!order.orderValueStatus || order.orderValueStatus === 'pending') {
      return `${baseClass} pending`;
    } else if (order.orderValueStatus === 'approved') {
      return `${baseClass} approved`;
    } else if (order.orderValueStatus === 'rejected') {
      return `${baseClass} rejected`;
    }
    return baseClass;
  };

  const renderDispatchedOrderContent = () => {
    if (!isDispatchedOrder) return null;

    return (
      <div className="order-card-content">
        {/* Condensed View */}
        <div className="condensed-view">
          <h4>
            {order.orderNumber && userRole !== 'Salesperson' && <span className="order-number">#{order.orderNumber}</span>} <span className="customer-name">{normalizePartyNameForDisplay(order.partyName)}</span>
            {userRole !== 'InventoryManager' && order.billURLs && order.billURLs.length > 0 && (
              <span className={getOrderValueClassName()} onClick={handleOrderValueClick}>
                Total: ₹{Number(order.orderValue || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            )}
          </h4>
          <div className="order-meta">
            <div className="timestamp">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {order.createdAt && (
                <span>
                  {t('createdAt')}: {formatDate(order.createdAt)}
                </span>
              )}
            </div>
            <div className="timestamp dispatched-time">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {order.dispatchedAt && (
                <span>
                  {t('dispatched')}: {formatDate(order.dispatchedAt)}
                </span>
              )}
            </div>
            {order.createdBy && (
              <span className="creator-name">
                {t('createdBy')} {creatorNames[order.createdBy] || t('unknownUser')}
              </span>
            )}
            {order.assignedTo && order.assignedTo !== order.createdBy && (
              <span className="assigned-name">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {t('assignedTo')} {creatorNames[order.assignedTo] || t('unknownUser')}
              </span>
            )}
          </div>
          <button 
            onClick={() => setIsExpanded(!isExpanded)} 
            className="expand-button"
          >
            {isExpanded ? t('showLess') : t('showMore')}
          </button>
        </div>

        {/* Expanded View */}
        {isExpanded && (
          <>
            <div className="expanded-view">
              <div className="order-info">
                <div className="info-group">
                  <div className="info-label">
                    {t('orderDetails')}
                  </div>
                  {renderOrderDetails(order)}
                </div>

                {order.additionalNotes && (
                  <div className="info-group">
                    <div className="info-label">
                      {t('form.additionalNotes')}
                    </div>
                    <div className="order-notes">
                      <div className="info-value">{order.additionalNotes}</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="button-group">
                {order.challanURLs && order.challanURLs.length > 0 && (
                  <button onClick={() => {
                    const processedUrls = order.challanURLs.map(url => viewChallan(url));
                    setChallanModalUrl(processedUrls);
                    setCurrentOrderId(order.id);
                  }} className="view">
                    {(userRole === 'InventoryManager' || userRole === 'BusinessOwner' || userRole === 'Accountant') 
                      ? t('actions.viewReplaceChallans')
                      : t('actions.viewChallans')
                    }
                  </button>
                )}
                {order.billURLs && order.billURLs.length > 0 && canViewBill() ? (
                  <button onClick={handleBillButtonClick} className="view">
                    {(userRole === 'Accountant' || userRole === 'BusinessOwner') 
                      ? t('actions.viewReplaceBills')
                      : t('actions.viewBills')
                    }
                  </button>
                ) : (userRole === 'Accountant' || userRole === 'BusinessOwner') && (
                  <button onClick={() => onUploadBill(order.id)} className="dispatch">
                    {t('actions.uploadBill')}
                  </button>
                )}
                {order.biltyURLs && order.biltyURLs.length > 0 ? (
                  <button onClick={() => {
                    const processedUrls = order.biltyURLs.map(url => viewBilty(url));
                    setBiltyModalUrl(processedUrls);
                    setCurrentOrderId(order.id);
                  }} className="view">
                    {(userRole === 'InventoryManager' || userRole === 'BusinessOwner' || userRole === 'Accountant') 
                      ? t('actions.viewReplaceBiltys')
                      : t('actions.viewBiltys')
                    }
                  </button>
                ) : (userRole === 'InventoryManager' || userRole === 'BusinessOwner' || userRole === 'Accountant') && (
                  <button onClick={() => onUploadBilty(order.id)} className="dispatch">
                    {t('actions.uploadBilty')}
                  </button>
                )}
                {order.productURLs && order.productURLs.length > 0 ? (
                  <button onClick={() => {
                    setProductModalUrl(order.productURLs);
                    setCurrentOrderId(order.id);
                  }} className="view">
                    {t('actions.viewProducts')}
                  </button>
                ) : userRole === 'InventoryManager' && (
                  <button onClick={() => onUploadProduct(order.id)} className="dispatch">
                    {t('actions.uploadProduct')}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderRegularOrderContent = () => {
    if (isDispatchedOrder) return null;

    return (
      <>
        <h4>
          {order.orderNumber && userRole !== 'Salesperson' && <span className="order-number">#{order.orderNumber}</span>} <span className="customer-name">{normalizePartyNameForDisplay(order.partyName)}</span>
          {userRole !== 'InventoryManager' && order.billURLs && order.billURLs.length > 0 && (
            <span className={getOrderValueClassName()} onClick={handleOrderValueClick}>
              Total: ₹{Number(order.orderValue || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          )}
        </h4>
        
        <div className="order-info">
          <div className="info-group">
            <div className="info-label">{t('orderDetails')}</div>
            {renderOrderDetails(order)}
          </div>

          {order.additionalNotes && (
            <div className="info-group">
              <div className="info-label">{t('form.additionalNotes')}</div>
              <div className="order-notes">
                <div className="info-value">{order.additionalNotes}</div>
              </div>
            </div>
          )}

          <div className="order-meta">
            <div className="timestamp">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {order.createdAt && (
                <span>{t('createdAt')}: {formatDate(order.createdAt)}</span>
              )}
            </div>
            {order.createdBy && (
              <span className="creator-name">
                {t('createdBy')} {creatorNames[order.createdBy] || t('unknownUser')}
              </span>
            )}
            {order.assignedTo && order.assignedTo !== order.createdBy && (
              <span className="assigned-name">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {t('assignedTo')} {creatorNames[order.assignedTo] || t('unknownUser')}
              </span>
            )}
          </div>
        </div>

        <div className="button-group">
          {order.status === 'PLACED' && (
            <>
              {(userRole === 'Accountant' || userRole === 'BusinessOwner') && (
                  <button onClick={() => onApprove(order.id)} className="approve">
                    {t('actions.approveOrder')}
                  </button>
              )}
              {(order.createdBy === user?.uid || order.assignedTo === user?.uid || userRole === 'Accountant' || userRole === 'BusinessOwner') && (
                <button onClick={() => onEdit(order)} className="edit">
                  {t('actions.editOrder')}
                </button>
              )}
              {(order.createdBy === user?.uid || order.assignedTo === user?.uid || userRole === 'Accountant' || userRole === 'BusinessOwner') && (
                <button onClick={() => onCancel(order.id)} className="cancel">
                  {t('actions.cancelOrder')}
                </button>
              )}
            </>
          )}

          {order.status === 'APPROVED' && (
            <>
              <div className="approved-buttons">
                {(userRole === 'InventoryManager' || userRole === 'Accountant' || userRole === 'BusinessOwner') && (
                  <>
                    {order.challanURLs && order.challanURLs.length > 0 ? (
                      <button onClick={() => {
                        const processedUrls = order.challanURLs.map(url => viewChallan(url));
                        setChallanModalUrl(processedUrls);
                        setCurrentOrderId(order.id);
                      }} className="view">
                        {(userRole === 'InventoryManager' || userRole === 'BusinessOwner' || userRole === 'Accountant') 
                          ? t('actions.viewReplaceChallans')
                          : t('actions.viewChallans')
                        }
                      </button>
                    ) : (userRole === 'InventoryManager' || userRole === 'Accountant' || userRole === 'BusinessOwner') && (
                      <button onClick={() => onUploadChallan(order.id)} className="dispatch">
                        {t('actions.uploadChallan')}
                      </button>
                    )}
                  </>
                )}
                {(userRole === 'Accountant' || userRole === 'BusinessOwner') && (
                  <>
                    {order.billURLs && order.billURLs.length > 0 ? (
                      <button onClick={handleBillButtonClick} className="view">
                        {(userRole === 'Accountant' || userRole === 'BusinessOwner') 
                          ? t('actions.viewReplaceBills')
                          : t('actions.viewBills')
                        }
                      </button>
                    ) : (
                      <button 
                        onClick={() => onUploadBill(order.id)} 
                        className="dispatch"
                      >
                        {t('actions.uploadBill')}
                      </button>
                    )}
                  </>
                )}
              </div>
              {(userRole === 'Accountant' || userRole === 'BusinessOwner' || userRole === 'Salesperson') && (
                <>
                  <div className="edit-button-row">
                    <button onClick={() => onEdit(order)} className="edit">
                      {t('actions.editOrder')}
                    </button>
                    {userRole !== 'Salesperson' && (
                    <button onClick={() => onCancel(order.id)} className="cancel">
                      {t('actions.cancelOrder')}
                    </button>
                    )}</div>
                  {((order.challanURLs && order.challanURLs.length > 0) || (order.billURLs && order.billURLs.length > 0)) && userRole !== 'Salesperson' && (
                    <div className="ready-dispatch-row">
                      <button onClick={() => onReadyToDispatch(order.id)} className="ready-dispatch-button">
                        {t('actions.readyToDispatch')}
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {order.status === 'READY_TO_DISPATCH' && (
            <div className="ready-dispatch-buttons">
              <div className="top-row">
                {order.challanURLs && order.challanURLs.length > 0 && (
                  <button onClick={() => {
                    const processedUrls = order.challanURLs.map(url => viewChallan(url));
                    setChallanModalUrl(processedUrls);
                    setCurrentOrderId(order.id);
                  }} className="view">
                    {t('actions.viewChallans')}
                  </button>
                )}
                {(userRole === 'Accountant' || userRole === 'BusinessOwner') ? (
                  order.billURLs && order.billURLs.length > 0 ? (
                    <button onClick={handleBillButtonClick} className="view">
                      {(userRole === 'Accountant' || userRole === 'BusinessOwner') 
                        ? t('actions.viewReplaceBills')
                        : t('actions.viewBills')
                      }
                    </button>
                  ) : (
                    <button onClick={() => onUploadBill(order.id)} className="dispatch">
                      {t('actions.uploadBill')}
                    </button>
                  )
                ) : (
                  order.billURLs && order.billURLs.length > 0 && canViewBill() && (
                    <button onClick={handleBillButtonClick} className="view">
                      {(userRole === 'Accountant' || userRole === 'BusinessOwner') 
                        ? t('actions.viewReplaceBills')
                        : t('actions.viewBills')
                      }
                    </button>
                  )
                )}
                {userRole === 'InventoryManager' && (
                  <>
                    {order.productURLs && order.productURLs.length > 0 ? (
                      <button onClick={() => {
                        setProductModalUrl(order.productURLs);
                        setCurrentOrderId(order.id);
                      }} className="view">
                        {t('actions.viewProducts')}
                      </button>
                    ) : (
                      <button onClick={() => onUploadProduct(order.id)} className="dispatch">
                        {t('actions.uploadProduct')}
                      </button>
                    )}
                  </>
                )}
              </div>
              {(userRole === 'InventoryManager' || userRole === 'BusinessOwner') && (
                <div className="bottom-row">
                  <button onClick={() => onDispatch(order.id)} className="ready-dispatch-button">
                    {t('actions.dispatchOrder')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <>
    <div className={`order-card ${isHighlighted ? 'order-card-highlighted' : ''}`}>
      <div className="order-status-pill-wrapper">
        <span className={`order-status-pill ${getStatusBadgeClass(order.status)}`}>
          {getStatusText(order.status, t)}
        </span>
      </div>
      <FileBackupStatus 
        organizationId={organizationData?.id}
        orderId={order.id}
        orderStatus={order.status}
        isDispatchedOnly={true}
        className="order-file-backup-status"
      />
      {isDispatchedOrder ? renderDispatchedOrderContent() : renderRegularOrderContent()}
    </div>
    </>
  );
}

export default OrderCard; 